import { ReactNode, useRef } from 'react'

import BeatLoader from 'react-spinners/BeatLoader'

import DownloadVideoIcon from '@/presentation/assets/svg/DownloadVideoIcon.svg'
import {
  ArrowLeft,
  ArrowRight
} from '@/presentation/assets/svg/ImageViewerArrows'
import Minimize from '@/presentation/assets/svg/Minimize.svg'
import MuteIcon from '@/presentation/assets/svg/MuteIcon.svg'
import PauseIcon from '@/presentation/assets/svg/PauseIcon.svg'
import PlayIcon from '@/presentation/assets/svg/PlayIcon.svg'
import UnmuteIcon from '@/presentation/assets/svg/UnmuteIcon.svg'
import { useVideoPlayer } from '@/services/hooks/useVideoPlayer'

import '@/presentation/components/FullscreenViewer/FullscreenVideo/styles.css'

interface FullscreenVideoProps {
  addIndex: () => void
  onClose: () => void
  paginationCounter: ReactNode
  subtractIndex: () => void
  videoUrl: string
}

const FullscreenVideo: React.FC<FullscreenVideoProps> = ({
  addIndex,
  onClose,
  paginationCounter,
  subtractIndex,
  videoUrl
}) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  const {
    currentTime,
    duration,
    handleLoadingStart,
    handleLoadingEnd,
    handleTogglePlay,
    handleToggleSound,
    handleVideoProgress,
    handleVideoManualProgress,
    loading,
    muted,
    playing,
    progress
  } = useVideoPlayer(videoRef)

  if (!videoUrl) return null

  return (
    <div
      id="video-player"
      className="group relative flex items-center justify-center rounded-2xl"
      onClick={event => event.stopPropagation()}
    >
      <video
        className="block min-h-[210px] rounded-2xl bg-black sm:min-h-[420px] lg:min-h-[738px]"
        src={videoUrl}
        ref={videoRef}
        onTimeUpdate={handleVideoProgress}
        onClick={handleTogglePlay}
        onLoadStart={handleLoadingStart}
        onLoadedData={handleLoadingEnd}
        muted={muted}
        loop
      />

      <img
        className="absolute right-4 top-4 cursor-pointer"
        src={Minimize}
        onClick={onClose}
      />

      <BeatLoader
        className="absolute inset-x-0 inset-y-1/2 z-50 m-auto -mt-5 !block text-center lg:-mt-3"
        color="#ffffff"
        size={50}
        loading={loading}
      />

      <div className="absolute inset-y-1/2 left-4" onClick={subtractIndex}>
        <ArrowLeft />
      </div>

      <div className="absolute inset-y-1/2 right-4" onClick={addIndex}>
        <ArrowRight />
      </div>

      <div className="absolute bottom-7 z-50 flex w-full flex-col items-center px-4 opacity-0 transition-opacity ease-in group-hover:opacity-100 sm:bottom-10">
        <div className="flex w-full items-center justify-center gap-x-4">
          <button
            className="cursor-pointer border-none bg-none outline-none"
            type="button"
            disabled={loading}
            onClick={handleTogglePlay}
          >
            {!playing ? <img src={PlayIcon} /> : <img src={PauseIcon} />}
          </button>

          <input
            className="h-1 w-full cursor-pointer appearance-none rounded"
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={event => !loading && handleVideoManualProgress(event)}
          />

          <button
            className="cursor-pointer border-none bg-none outline-none"
            type="button"
            disabled={loading}
            onClick={handleToggleSound}
          >
            {!muted ? <img src={UnmuteIcon} /> : <img src={MuteIcon} />}
          </button>

          <a href={videoUrl} target="_blank" rel="noreferrer" download>
            <img src={DownloadVideoIcon} />
          </a>
        </div>

        <div className="text-xs text-white">
          {currentTime.minutes}:{currentTime.seconds} / {duration.minutes}:
          {duration.seconds}
        </div>
      </div>

      <div className="absolute inset-x-1/2 bottom-2 flex justify-center text-xs text-white sm:text-base">
        {paginationCounter}
      </div>
    </div>
  )
}

export default FullscreenVideo
